import { Skeleton } from '@mantine/core'
import { ResponsiveLine } from '@nivo/line'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import {
  SegmentedTabs,
  type SegmentedTabItem,
} from 'src/components/Core/SegmentedTabs'

import AnalyticsCard from './AnalyticsCard'

// GraphQL `DateTime` fields are UTC-midnight ISO strings. Without this
// plugin, `dayjs(iso)` parses in local time and users west of UTC see the
// previous calendar day on x-axis labels and tooltips. Established codebase
// pattern (see EmploymentDetailsDrawer, DocumentPreview, MonthYearInput).
dayjs.extend(utc)

/** Local type — kept domain-neutral so this component doesn't depend on
 *  any one feature's GraphQL types. Structurally compatible with
 *  `PipelinePeriodBucket` from types/graphql. */
export type TrendBucket = 'DAY' | 'WEEK' | 'MONTH'

const BUCKET_TABS: ReadonlyArray<SegmentedTabItem<TrendBucket>> = [
  { value: 'DAY', label: 'Day' },
  { value: 'WEEK', label: 'Week' },
  { value: 'MONTH', label: 'Month' },
]

const DEFAULT_COLOR = '#93C5FD' // sky-300, soft pastel
// Tuned so this card's overall height matches a 5-row StatRowsCardView
// (Pipeline Summary, Call Status Summary). When these sit side-by-side
// in a grid, anything taller pushes the row baseline down on the chart
// side and reads as bottom padding.
const CHART_HEIGHT = 360

export interface TrendPoint {
  /** ISO date marking the start of the bucket. */
  periodStart: string
  count: number
}

export interface TrendSeries {
  /** Stable key, e.g. an enum value or slug. */
  id: string
  /** Human-readable label (used in tooltip + legend). */
  label: string
  /** Pastel hex; line uses this. */
  color: string
  /** Time-bucketed points for this series. All series should share the
   *  same periodStart values for clean alignment. */
  points: ReadonlyArray<TrendPoint>
}

export interface TrendData {
  bucket: TrendBucket
  series: ReadonlyArray<TrendSeries>
}

export interface TrendChartViewProps {
  title: string
  info?: string
  data: TrendData | undefined
  loading?: boolean
  bucket: TrendBucket
  onBucketChange: (bucket: TrendBucket) => void
  /** Singular noun for the tooltip ("applicant" → "5 applicants"). */
  tooltipUnit?: string
  /** Message when there are zero points across all series. */
  emptyMessage?: string
}

const formatXLabel = (iso: string, bucket: TrendBucket): string => {
  const d = dayjs.utc(iso)
  if (bucket === 'DAY') return d.format('M/D')
  if (bucket === 'WEEK') return d.format('M/D')
  // Include 2-digit year so multi-year MONTH ranges don't collide on the
  // point x-scale (e.g. Apr 2025 + Apr 2026 → "Apr 25" vs "Apr 26").
  return d.format('MMM YY')
}

const formatTooltipDate = (iso: string, bucket: TrendBucket): string => {
  const d = dayjs.utc(iso)
  if (bucket === 'DAY') return d.format('ddd, MMM D')
  if (bucket === 'WEEK') return `Week of ${d.format('MMM D')}`
  return d.format('MMMM YYYY')
}

/** Pick a sparse subset of x-labels so dense ranges don't collide. */
const thinXTicks = (labels: ReadonlyArray<string>): ReadonlyArray<string> => {
  const n = labels.length
  if (n <= 7) return labels
  const target = n <= 14 ? 7 : 6
  const step = Math.max(1, Math.floor((n - 1) / (target - 1)))
  const picked = new Set<number>([0, n - 1])
  for (let i = step; i < n - 1; i += step) picked.add(i)
  return [...picked].sort((a, b) => a - b).map((i) => labels[i])
}

/** Returns the union of x-labels across series so the axis covers all
 *  buckets even if a particular series has gaps. Series should already
 *  share periodStarts in practice; this is just defensive. */
const collectAxisLabels = (
  series: ReadonlyArray<TrendSeries>,
  bucket: TrendBucket
): ReadonlyArray<string> => {
  const seen = new Map<string, string>() // periodStart → formatted
  for (const s of series) {
    for (const p of s.points) {
      if (!seen.has(p.periodStart)) {
        seen.set(p.periodStart, formatXLabel(p.periodStart, bucket))
      }
    }
  }
  return [...seen.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, label]) => label)
}

export const TrendChartView = ({
  title,
  info,
  data,
  loading = false,
  bucket,
  onBucketChange,
  tooltipUnit = 'item',
  emptyMessage = 'No data in this period',
}: TrendChartViewProps) => {
  const headerControls = (
    <SegmentedTabs
      tabs={BUCKET_TABS}
      value={bucket}
      onChange={onBucketChange}
      ariaLabel="Bucket size"
    />
  )

  if (loading && !data) {
    return (
      <AnalyticsCard title={title} info={info} headerChildren={headerControls}>
        <div style={{ height: CHART_HEIGHT }}>
          <Skeleton height="100%" radius="md" />
        </div>
      </AnalyticsCard>
    )
  }

  const series = data?.series ?? []
  const totalPoints = series.reduce((s, x) => s + x.points.length, 0)

  if (totalPoints === 0) {
    return (
      <AnalyticsCard title={title} info={info} headerChildren={headerControls}>
        <div className="flex h-[280px] items-center justify-center text-sm text-gray-400">
          {emptyMessage}
        </div>
      </AnalyticsCard>
    )
  }

  const isMulti = series.length > 1
  const axisLabels = collectAxisLabels(series, bucket)

  const lineData = series.map((s) => ({
    id: s.label,
    color: s.color,
    data: s.points.map((p) => ({
      x: formatXLabel(p.periodStart, bucket),
      y: p.count,
      rawDate: p.periodStart,
    })),
  }))

  const seriesColors = series.map((s) => s.color)
  const seriesById = new Map(series.map((s) => [s.label, s.color] as const))

  // Bottom margin reserves room for a horizontal legend when multi-series.
  const margin = isMulti
    ? { top: 20, right: 24, bottom: 72, left: 48 }
    : { top: 20, right: 24, bottom: 40, left: 48 }

  return (
    <AnalyticsCard title={title} info={info} headerChildren={headerControls}>
      <div style={{ height: CHART_HEIGHT }}>
        <ResponsiveLine
          // Force a clean remount when the bucket changes. Nivo animates the
          // line as a single <path> via d-attribute interpolation — when the
          // categorical x-scale's category set changes wholesale (DAY → WEEK
          // → MONTH), there's no valid mapping between the old and new path
          // strings, so the line renders stale/broken while the individually
          // animated point markers correctly snap to the new positions.
          key={bucket}
          data={lineData}
          margin={margin}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', min: 0, max: 'auto' }}
          curve="monotoneX"
          colors={seriesColors.length ? seriesColors : [DEFAULT_COLOR]}
          lineWidth={2}
          // Area fill only when there's a single series — overlapping
          // translucent fills mud each other into an unreadable wash.
          enableArea={!isMulti}
          areaOpacity={0.18}
          enablePoints
          pointSize={4}
          pointColor="#FFFFFF"
          pointBorderWidth={1.5}
          pointBorderColor={{ from: 'serieColor' }}
          enableGridX={false}
          enableGridY
          axisBottom={{
            tickSize: 0,
            tickPadding: 8,
            tickValues: thinXTicks(axisLabels),
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 6,
            tickValues: 4,
            format: (v: number) =>
              v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v),
          }}
          theme={{
            axis: { ticks: { text: { fill: '#9CA3AF', fontSize: 11 } } },
            grid: { line: { stroke: '#E5E7EB', strokeDasharray: '2 3' } },
          }}
          legends={
            isMulti
              ? [
                {
                  anchor: 'bottom',
                  direction: 'row',
                  justify: false,
                  translateX: 0,
                  translateY: 56,
                  itemWidth: 110,
                  itemHeight: 18,
                  itemsSpacing: 8,
                  symbolSize: 8,
                  symbolShape: 'circle',
                  itemTextColor: '#4B5563',
                },
              ]
              : []
          }
          enableSlices="x"
          sliceTooltip={({ slice }) => {
            if (slice.points.length === 0) return null
            const firstRaw = slice.points[0].data as unknown as {
              rawDate: string
            }
            const sorted = [...slice.points].sort(
              (a, b) => Number(b.data.yFormatted) - Number(a.data.yFormatted)
            )
            return (
              <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-[11px] shadow-md">
                <div className="mb-1 text-gray-500">
                  {formatTooltipDate(firstRaw.rawDate, bucket)}
                </div>
                <div className="flex flex-col gap-0.5">
                  {sorted.map((p) => {
                    const id = String(p.serieId)
                    const n = Number(p.data.yFormatted)
                    return (
                      <div
                        key={id}
                        className="flex items-center justify-between gap-3"
                      >
                        <span className="flex items-center gap-1.5">
                          <span
                            className="inline-block h-2 w-2 flex-shrink-0 rounded-full"
                            style={{
                              background: seriesById.get(id) ?? p.serieColor,
                            }}
                          />
                          <span className="text-gray-800">{id}</span>
                        </span>
                        <span className="font-mono font-semibold tabular-nums text-gray-900">
                          {n.toLocaleString()}
                          {!isMulti && (
                            <span className="ml-1 font-sans text-gray-500">
                              {tooltipUnit}
                              {n === 1 ? '' : 's'}
                            </span>
                          )}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          }}
        />
      </div>
    </AnalyticsCard>
  )
}
